import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Typography from "style-guide/component/Typography";
import Button from "style-guide/component/Button";
import { CheckCircleOutlined } from "@ant-design/icons";

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(3);
  
  // Get plan details from navigation state
  const planDetails = location.state?.planDetails;
  const userEmail = location.state?.userEmail;

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer); 
          window.location.href = "/home";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleGoToHome = () => {
    window.location.href = "/home";
  };

  const isCreditsPurchase = planDetails?.type === 'credit';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-xl shadow-xl p-10 text-center transform animate-fade-in-up">
          {/* Success Icon */}
          <div className="mb-8">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
              <CheckCircleOutlined className="text-8xl text-green-600 animate-bounce" />
            </div>
          </div>

          {/* Success Message */}
          <div className="mb-8 animate-fade-in-delayed">
            <Typography variant="Heading 2" className="text-gray-900 mb-3">
              Payment Successful!
            </Typography>
            
            <Typography variant="LG" className="text-gray-600 leading-relaxed">
              {isCreditsPurchase 
                ? "Your credits have been added to your account."
                : "Your subscription has been activated successfully."
              }
            </Typography>
          </div>

          {/* Plan Details */}
          {planDetails && (
            <div className="bg-[#0F428D] bg-opacity-5 rounded-xl p-6 mb-8 border border-[#0F428D] border-opacity-20 transform hover:scale-105 transition-transform duration-200 animate-slide-in-left">
              <Typography variant="Heading 4" className="font-semibold text-[#0F428D] mb-2">
                {planDetails?.name}
              </Typography>
              <Typography variant="LG" className="text-gray-700 font-medium">
                {isCreditsPurchase ? planDetails?.price : planDetails?.monthlyPrice}
              </Typography>
              {isCreditsPurchase && planDetails?.credits && (
                <Typography variant="Base" className="text-gray-600 mt-2">
                  {planDetails?.credits} Added
                </Typography>
              )}
            </div>
          )}

          {/* Email Confirmation */}
          {userEmail && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 transform hover:bg-gray-100 transition-colors duration-200 animate-slide-in-right">
              <Typography variant="Base" className="text-gray-600 mb-2">
                Confirmation sent to:
              </Typography>
              <Typography variant="LG" className="font-semibold text-gray-800">
                {userEmail}
              </Typography>
            </div>
          )}

          {/* Countdown */}
          <div className="mb-8 animate-pulse-subtle">
            <div className="inline-flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
              <div className="w-2 h-2 bg-[#0F428D] rounded-full animate-ping" />
              <Typography variant="Base" className="text-[#0F428D] font-medium">
                Redirecting in {countdown} seconds...
              </Typography>
            </div>
          </div>

          {/* Action Button */}
          <div className="animate-fade-in-delayed">
            <Button
              type="Primary"
              buttonText="Go to Dashboard"
              onClick={handleGoToHome}
              className="w-full transform hover:scale-105 transition-transform duration-200"
              size="Large"
            />
          </div>

          {/* Security Info */}
          <div className="mt-8 pt-6 border-t border-gray-200 animate-fade-in-delayed">
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <Typography variant="SM" className="text-gray-500">
                Secured by Stripe
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
